import { Injectable } from "@angular/core";
import { BaseValidationResult } from "@ha/ui/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Navigate } from "@ngxs/router-plugin";
import { Action, Actions, ofActionSuccessful, State, StateContext, Store } from "@ngxs/store";
import { produce } from "immer";
import { Observable, of } from "rxjs";
import { concatMap, map, take, tap } from "rxjs/operators";

import { PermitPaths } from "../../permit-paths";
import { PermitStepActions } from "../../permit/states/permit-step.action";
import { CheckpointAnswerCommand, QuestionAnswerCommand, SetSafetyEquipmentAnswersCommand } from "../models/create-permit-commands";
import {
    CustomerQuestion,
    CustomerQuestionAnswer,
    FixedQuestion,
    FlammableMaterialMeasurement,
    QuestionAnswer,
    QuestionAnswerType,
    QuestionDto,
} from "../models/question.model";
import { PermitCreateService } from "../services/permit-create.service";
import { PermitQuestionService } from "../services/permit-question.service";
import { PermitCreateSelectors } from "./permit-create.selectors";
import { PermitQuestionStorageState, SaveLocalQuestion } from "./permit-question-storage.state";
import { PermitQuestionActions } from "./permit-question.actions";

export type PermitQuestionNavigationSource = "current" | "next" | "previous";

export interface PermitQuestionStateModel {
    customerQuestion: CustomerQuestion;
    fixedQuestion: FixedQuestion;

    permitId: number;
    subscriptionId: number;
    templateId: number;

    loadingQuestion: boolean;
    navigationSource: PermitQuestionNavigationSource;
}

const defaults: PermitQuestionStateModel = {
    customerQuestion: undefined,
    fixedQuestion: undefined,

    permitId: undefined,
    subscriptionId: undefined,
    templateId: undefined,

    loadingQuestion: undefined,
    navigationSource: "current",
};

@UntilDestroy()
@Injectable()
@State<PermitQuestionStateModel>({
    name: "permitQuestion",
    defaults: defaults,
})
export class PermitQuestionState {
    constructor(
        private store: Store,
        private permitCreateService: PermitCreateService,
        private permitQuestionService: PermitQuestionService,
        private actions$: Actions,
    ) {}

    @Action(PermitQuestionActions.GetCurrentQuestion)
    public getCurrentQuestion(ctx: StateContext<PermitQuestionStateModel>): Observable<QuestionDto> {
        ctx.setState(produce((draft) => {
            draft.loadingQuestion = true;
            draft.navigationSource = "current";
        }));

        const state = ctx.getState();

        return this.permitQuestionService.getCurrentQuestion(state.permitId, this.getCurrentQuestionId(state)).pipe(
            tap((result: QuestionDto) => {
                ctx.setState(produce(ctx.getState(), (draft) => {
                    if (result.fixedQuestion && (result.fixedQuestion !== null)) {
                        draft.fixedQuestion = result.fixedQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.fixedQuestion.id));
                    } else {
                        draft.fixedQuestion = undefined;
                    }

                    if (result.customerQuestion && (result.customerQuestion !== null)) {
                        draft.customerQuestion = result.customerQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.customerQuestion.id));
                    } else {
                        draft.customerQuestion = undefined;
                    }

                    if (!draft.customerQuestion && !draft.fixedQuestion) {
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, undefined));
                        ctx.dispatch(new PermitStepActions.GoForward(state.permitId, this.getCurrentQuestionId(state)));
                    }

                    draft.loadingQuestion = false;
                }));
            }),
        );
    }

    @Action(PermitQuestionActions.GetPermitAndCurrentQuestion)
    public getPermitAndCurrentQuestion(
        ctx: StateContext<PermitQuestionStateModel>,
        action: PermitQuestionActions.GetPermitAndCurrentQuestion,
    ): Observable<void> {
        const permit = this.store.selectSnapshot(PermitCreateSelectors.permit);
        const requestedPermitId = action.permitId;

        if (!permit || (requestedPermitId !== undefined && permit.id !== requestedPermitId)) {
            return of(undefined);
        }

        ctx.setState(produce(ctx.getState(), (draft) => {
            draft.permitId = permit.id;
            draft.subscriptionId = permit.subscriptionId;
            draft.templateId = permit.customerPermitTemplateId;
        }));

        return ctx.dispatch(new PermitQuestionActions.GetCurrentQuestion());
    }

    @Action(PermitQuestionActions.GetNextQuestion)
    public getNextQuestion(ctx: StateContext<PermitQuestionStateModel>): Observable<QuestionDto> {
        ctx.setState(produce((draft) => {
            draft.loadingQuestion = true;
            draft.navigationSource = "next";
        }));
        const state = ctx.getState();
        const currentQuestionId = this.getCurrentQuestionId(state);

        return this.permitQuestionService.getNextQuestion(state.permitId, currentQuestionId).pipe(
            tap((result: QuestionDto) => {
                ctx.setState(produce(ctx.getState(), (draft) => {
                    if (result.fixedQuestion && (result.fixedQuestion !== null)) {
                        draft.fixedQuestion = result.fixedQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.fixedQuestion.id));
                    } else {
                        draft.fixedQuestion = undefined;
                    }

                    if (result.customerQuestion && (result.customerQuestion !== null)) {
                        draft.customerQuestion = result.customerQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.customerQuestion.id));
                    } else {
                        draft.customerQuestion = undefined;
                    }

                    if (!draft.customerQuestion && !draft.fixedQuestion) {
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, undefined));
                        ctx.dispatch(new PermitStepActions.GoForward(state.permitId, currentQuestionId));
                    }

                    draft.loadingQuestion = false;
                }));
            }),
        );
    }

    @Action(PermitQuestionActions.GetPreviousQuestion)
    public getPreviousQuestion(ctx: StateContext<PermitQuestionStateModel>): Observable<any> {
        ctx.setState(produce((draft) => {
            draft.loadingQuestion = true;
            draft.navigationSource = "previous";
        }));
        const state = ctx.getState();
        const currentQuestionId = this.getCurrentQuestionId(state);

        return this.permitQuestionService.getPreviousQuestion(state.permitId, currentQuestionId).pipe(
            concatMap((result: QuestionDto) => {
                const returnToPermitId = this.store.selectSnapshot(PermitCreateSelectors.permit)?.returnToPermitId;

                ctx.setState(produce(ctx.getState(), (draft) => {
                    if (result.fixedQuestion && (result.fixedQuestion !== null)) {
                        draft.fixedQuestion = result.fixedQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.fixedQuestion.id));
                    } else {
                        draft.fixedQuestion = undefined;
                    }

                    if (result.customerQuestion && (result.customerQuestion !== null)) {
                        draft.customerQuestion = result.customerQuestion;
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, result.customerQuestion.id));
                    } else {
                        draft.customerQuestion = undefined;
                    }

                    if (!draft.customerQuestion && !draft.fixedQuestion) {
                        ctx.dispatch(new SaveLocalQuestion(state.permitId, undefined));
                    }

                    draft.loadingQuestion = false;
                }));

                if (!result.customerQuestion && !result.fixedQuestion) {
                    if (returnToPermitId !== undefined && returnToPermitId !== null) {
                        return this.permitCreateService.removePlannedPermit(state.permitId).pipe(
                            concatMap((removeResult) => {
                                if (!removeResult.success) {
                                    throw new Error("removePlannedPermit was not successful");
                                }

                                return ctx.dispatch(new PermitStepActions.GoBackwards(returnToPermitId)).pipe(
                                    concatMap(() => ctx.dispatch(new Navigate([PermitPaths.Root, returnToPermitId.toString()]))),
                                );
                            }),
                        );
                    }

                    return ctx.dispatch(new PermitStepActions.GoBackwards(state.permitId, currentQuestionId));
                }

                return of(result);
            }),
        );
    }

    @Action(PermitQuestionActions.ResetToDefault)
    public resetToDefault(ctx: StateContext<PermitQuestionStateModel>): void {
        ctx.setState(defaults);
    }

    @Action(PermitQuestionActions.SetFixedOrCustomerQuestionAnswer)
    public setQuestionOrCheckpointAnswer(
        ctx: StateContext<PermitQuestionStateModel>,
        action: PermitQuestionActions.SetFixedOrCustomerQuestionAnswer,
    ): void {
        const currentState = ctx.getState();

        this.actions$.pipe(
            ofActionSuccessful(PermitQuestionActions.SaveCheckpointAnswer, PermitQuestionActions.SaveQuestionAnswer),
            take(1),
            untilDestroyed(this),
            tap(() => {
                ctx.dispatch(new PermitQuestionActions.GetNextQuestion());
            }),
        ).subscribe();

        if (currentState.customerQuestion) {
            if (action.answer.answer) {
                this.store.dispatch(
                    new PermitQuestionActions.SaveCheckpointAnswer(
                        currentState.customerQuestion.id,
                        CustomerQuestionAnswer[action.answer.answer.toString() as keyof typeof CustomerQuestionAnswer],
                    ),
                );
            }
        } else if (currentState.fixedQuestion) {
            this.store.dispatch(new PermitQuestionActions.SaveQuestionAnswer(action.answer));
        }
    }

    @Action(PermitQuestionActions.SaveQuestionAnswer)
    public saveQuestionAnswer(
        ctx: StateContext<PermitQuestionStateModel>,
        action: PermitQuestionActions.SaveQuestionAnswer,
    ): Observable<BaseValidationResult> {
        const state = ctx.getState();
        if (state.fixedQuestion?.answerType === QuestionAnswerType.SafetyEquipmentSelection) {
            const command: SetSafetyEquipmentAnswersCommand = {
                permitId: state.permitId,
                questionId: action.answer.questionId,
                answers: action.answer.safetyEquipmentAnswers ?? [],
            };

            return this.permitCreateService.setSafetyEquipmentAnswers(command).pipe(tap((result) => {
                if (!result.success) {
                    throw new Error("setSafetyEquipmentAnswers was not successful");
                }
            }));
        }

        const requestedFlammableMaterialMeasurements = this.getRequestedFlammableMaterialMeasurements(
            state.fixedQuestion,
            action.answer,
        );

        const command: QuestionAnswerCommand = {
            permitId: state.permitId,
            fixedQuestionAnswer: {
                answer: action.answer.answer
                    ? CustomerQuestionAnswer[action.answer.answer.toString() as keyof typeof CustomerQuestionAnswer]
                    : undefined,
                questionId: action.answer.questionId,
                selectedOptionIds: action.answer.selectedOptionIds,
                textAnswer: action.answer.textAnswer,
                numberAnswer: action.answer.numberAnswer,
                dateTimeAnswer: action.answer.dateTimeAnswer,
                conditionText: action.answer.conditionText,
                currentFlammableMaterialMeasurements: requestedFlammableMaterialMeasurements,
            },
        };

        return this.saveFlammableMaterialMeasurementsIfNeeded(state, requestedFlammableMaterialMeasurements).pipe(
            concatMap(() => this.permitCreateService.setFixedQuestionAnswer(command)),
            tap((result) => {
                if (!result.success) {
                    throw new Error("setFixedQuestionAnswer was not successful");
                }
            }),
        );
    }

    @Action(PermitQuestionActions.SaveCheckpointAnswer)
    public saveCheckpointAnswer(
        ctx: StateContext<PermitQuestionStateModel>,
        action: PermitQuestionActions.SaveCheckpointAnswer,
    ): Observable<BaseValidationResult> {
        const state = ctx.getState();

        const command: CheckpointAnswerCommand = {
            permitId: state.permitId,
            customerCheckpointAnswer: {
                answer: action.answer,
                checkpointId: action.checkpointId,
            },
        };

        return this.permitCreateService.setCheckpointAnswer(command).pipe(tap((result) => {
            if (!result.success) {
                throw new Error("setCheckpointAnswer was not successful");
            }
        }));
    }

    private getCurrentQuestionId(state: PermitQuestionStateModel): number {
        const localPermitId = this.store.selectSnapshot(PermitQuestionStorageState.permitId);
        const localQuestionId = this.store.selectSnapshot(PermitQuestionStorageState.questionId);

        if (state.permitId == localPermitId) {
            if (localQuestionId) {
                return localQuestionId;
            }

            if (state.customerQuestion) {
                return state.customerQuestion.id;
            }

            if (state.fixedQuestion) {
                return state.fixedQuestion.id;
            }
        }

        return undefined;
    }

    private saveFlammableMaterialMeasurementsIfNeeded(
        state: PermitQuestionStateModel,
        currentFlammableMaterialMeasurements: FlammableMaterialMeasurement[] = [],
    ): Observable<boolean> {
        if (
            !state.fixedQuestion
            || (
                state.fixedQuestion.answerType !== QuestionAnswerType.SubstanceSelection
                && state.fixedQuestion.answerType !== QuestionAnswerType.SubstanceMeasurement
            )
        ) {
            return of(true);
        }

        return this.permitCreateService.setFlammableMaterialMeasurements({
            permitId: state.permitId,
            measurements: currentFlammableMaterialMeasurements.map((measurement) => ({
                flammableMaterialId: measurement.flammableMaterialId,
                value: measurement.value,
            })),
        }).pipe(map((result) => result.success));
    }

    private getRequestedFlammableMaterialMeasurements(
        fixedQuestion: FixedQuestion,
        answer: QuestionAnswer,
    ): FlammableMaterialMeasurement[] {
        if (!fixedQuestion) {
            return [];
        }

        if (fixedQuestion.answerType === QuestionAnswerType.SubstanceSelection) {
            return (answer.selectedFlammableMaterialIds ?? []).map((flammableMaterialId) => ({
                flammableMaterialId: flammableMaterialId,
                value: 0,
            }));
        }

        if (fixedQuestion.answerType === QuestionAnswerType.SubstanceMeasurement) {
            return answer.currentFlammableMaterialMeasurements ?? [];
        }

        return [];
    }
}
